import React, { useState } from 'react';
import { BanknoteArrowUp, ChevronLeft } from 'lucide-react';
import Button from '../../components/ui/buttons/Button';
import SearchInput from '../../components/ui/inputs/SearchInput';
import DashboardTab from '../planner/client/dashboard/DashboardTab';

import InvestmentFundsContent from './assets/InvestmentFundsContent';
import InvestmentFundsSidebar from './assets/InvestmentFundsSidebar';
import useInvestmentFunds from './assets/useInvestmentFunds';

import PensionFundsContent from './assets/PensionFundsContent';
import PensionFundsSidebar from './assets/PensionFundsSidebar';
import usePensionFunds from './assets/usePensionFunds';

import EquitiesContent from './assets/EquitiesContent';
import EquitiesSidebar from './assets/EquitiesSidebar';
import useEquities from './assets/useEquities';

import OffshoreSymbolsContent from './assets/OffshoreSymbolsContent';
import OffshoreSymbolsSidebar from './assets/OffshoreSymbolsSidebar';
import useOffshoreSymbols from './assets/useOffshoreSymbols';

const tabs = [
  { key: 'investment_funds', label: 'FUNDOS DE INVESTIMENTO' },
  { key: 'pension', label: 'PREVIDÊNCIA' },
  { key: 'equities', label: 'RENDA VARIÁVEL (AÇÕES/FIIS)' },
  { key: 'offshore_symbols', label: 'INTERNACIONAIS' },
];

const validTabKeys = tabs.map((tab) => tab.key);

const getTabFromUrl = () => {
  const url = new URL(window.location.href);
  const tabFromUrl = url.searchParams.get('tab');

  if (tabFromUrl && validTabKeys.includes(tabFromUrl)) return tabFromUrl;

  return tabs[0].key;
};

const emptyPagination = {
  currentPage: 1,
  totalPages: 1,
  totalCount: 0,
  perPage: 10,
};

const emptyTabData = {
  initialAssets: [],
  initialPagination: emptyPagination,
  initialBases: [],
  initialFilterOptions: {},
};

const Assets = ({
  assets: initialAssets = [],
  pagination: initialPagination = emptyPagination,
  bases: initialBases = [],
  filterOptions: initialFilterOptions = {},
}) => {
  const [initialTab] = useState(getTabFromUrl);
  const [activeTab, setActiveTab] = useState(getTabFromUrl);
  const [search, setSearch] = useState('');

  const handleTabChange = (key) => {
    setActiveTab(key);
    setSearch('');

    const url = new URL(window.location);
    url.searchParams.set('tab', key);
    window.history.pushState({}, '', url);
  };

  const investmentFundsInitialData =
    initialTab === 'investment_funds'
      ? {
          initialAssets,
          initialPagination,
          initialBases,
          initialFilterOptions,
        }
      : emptyTabData;

  const pensionFundsInitialData =
    initialTab === 'pension'
      ? {
          initialAssets,
          initialPagination,
          initialBases,
          initialFilterOptions,
        }
      : emptyTabData;

  const equitiesInitialData =
    initialTab === 'equities'
      ? {
          initialAssets,
          initialPagination,
          initialBases,
          initialFilterOptions,
        }
      : emptyTabData;

  const offshoreSymbolsInitialData =
    initialTab === 'offshore_symbols'
      ? {
          initialAssets,
          initialPagination,
          initialBases,
          initialFilterOptions,
        }
      : emptyTabData;

  const investmentFundsState = useInvestmentFunds({
    ...investmentFundsInitialData,
    search,
    enabled: activeTab === 'investment_funds',
  });

  const pensionFundsState = usePensionFunds({
    ...pensionFundsInitialData,
    search,
    enabled: activeTab === 'pension',
  });

  const equitiesState = useEquities({
    ...equitiesInitialData,
    search,
    enabled: activeTab === 'equities',
  });

  const offshoreSymbolsState = useOffshoreSymbols({
    ...offshoreSymbolsInitialData,
    search,
    enabled: activeTab === 'offshore_symbols',
  });

  const renderHeader = () => {
    return (
      <>
        <div className="flex justify-between items-center mb-4">
          <span className="flex items-center gap-2">
            <BanknoteArrowUp className="text-tealMedium" size={20} strokeWidth={1.5} />
            <h1 className="text-2xl font-semibold text-blueDarker">Gerenciar Ativos</h1>
          </span>

          <div className="flex items-center gap-3">
            <Button icon={ChevronLeft} onClick={() => window.history.back()}>
              VOLTAR
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-6 min-w-0">
            {tabs.map((tab) => (
              <DashboardTab
                key={tab.key}
                label={tab.label}
                active={activeTab === tab.key}
                completed={tab.completed}
                onClick={() => handleTabChange(tab.key)}
              />
            ))}
          </div>

          <div className="w-96 flex-none">
            <SearchInput
              name="search"
              placeholder="Pesquisar por nome, CNPJ, ticker, símbolo..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </>
    );
  };

  if (activeTab === 'investment_funds') {
    return (
      <div className="p-0">
        <div className="grid gap-x-5 items-start" style={{ gridTemplateColumns: 'minmax(0, 1fr) 420px' }}>
          <div className="min-w-0">
            {renderHeader()}

            <InvestmentFundsContent {...investmentFundsState} />
          </div>

          <InvestmentFundsSidebar {...investmentFundsState} />
        </div>
      </div>
    );
  }

  if (activeTab === 'pension') {
    return (
      <div className="p-0">
        <div className="grid gap-x-5 items-start" style={{ gridTemplateColumns: 'minmax(0, 1fr) 420px' }}>
          <div className="min-w-0">
            {renderHeader()}

            <PensionFundsContent {...pensionFundsState} />
          </div>

          <PensionFundsSidebar {...pensionFundsState} />
        </div>
      </div>
    );
  }

  if (activeTab === 'equities') {
    return (
      <div className="p-0">
        <div className="grid gap-x-5 items-start" style={{ gridTemplateColumns: 'minmax(0, 1fr) 420px' }}>
          <div className="min-w-0">
            {renderHeader()}

            <EquitiesContent {...equitiesState} />
          </div>

          <EquitiesSidebar {...equitiesState} />
        </div>
      </div>
    );
  }

  if (activeTab === 'offshore_symbols') {
    return (
      <div className="p-0">
        <div className="grid gap-x-5 items-start" style={{ gridTemplateColumns: 'minmax(0, 1fr) 420px' }}>
          <div className="min-w-0">
            {renderHeader()}

            <OffshoreSymbolsContent {...offshoreSymbolsState} />
          </div>

          <OffshoreSymbolsSidebar {...offshoreSymbolsState} />
        </div>
      </div>
    );
  }

  return null;
};

export default Assets;
